/*
 * Drag lifecycle monitor + stuck-drag watchdog.
 *
 * Background: an HTML5 drag puts Chrome into a browser-wide modal drag loop
 * that only exits when the mouse release (or Esc) is delivered. Remote
 * desktop tools (AnyDesk etc.) can lose that release, leaving the whole
 * browser ignoring clicks while wheel scrolling still works — reported by an
 * SFP user as "dragging a document freezes Chrome" (Sep 2026). Locally the
 * drag pipeline always measured healthy (dragend fired 7/7, handlers < 71ms),
 * so this module exists to (a) tell the user how to recover and (b) capture
 * evidence when it happens in the field.
 *
 * What it does:
 *  - logs every drag lifecycle transition (start/end/drop/enter/leave) with
 *    timing, coordinates and source-element state into a ring buffer
 *  - summarises high-frequency dragover/drag activity per second instead of
 *    per event, so the log stays readable and cheap
 *  - watchdog: if a drag goes silent (no dragover/drag heartbeat) without a
 *    dragend/drop, it logs a STALL and shows a snack telling the user to
 *    press Esc
 *
 * Reading the evidence on a client machine (F12 console):
 *  - live entries are tagged [DragMonitor]
 *  - window.__dragLog            -> the ring buffer (newest last)
 *  - window.__dragLogDump()      -> JSON string of the buffer for copy/paste
 */

const snack = require('@/snack');

// Same singleton class vuedraggable drives — gives us Sortable.active /
// Sortable.ghost / Sortable.dragged to inspect and force-finish a wedged drag.
let Sortable = null;
try {
    Sortable = require('sortablejs');
    Sortable = Sortable.default || Sortable;
} catch (e) { /* not installed — sweeps just skip the Sortable checks */ }

const MAX_LOG = 400;
const HEARTBEAT_CHECK_MS = 1000;
const STALL_AFTER_MS = 3000;   // no heartbeat for this long => stalled
const HARD_STUCK_AFTER_MS = 15000;
const SWEEP_MS = 2000;         // stuck-Sortable sweep cadence
const SWEEPS_BEFORE_HEAL = 3;  // ~6s of stuck artifacts before auto-healing

const log = [];
let seq = 0;
let session = null;   // the active drag, null when no drag is open
let watchdog = null;

function now() { return Date.now(); }

function describeEl(el) {
    if (!el || !el.tagName) return null;
    return {
        tag: el.tagName,
        cls: String(el.className && el.className.baseVal !== undefined ? el.className.baseVal : el.className || '').slice(0, 80),
        connected: el.isConnected
    };
}

function push(type, detail, echo = false) {
    const entry = {n: ++seq, t: new Date().toISOString(), type, ...detail};
    log.push(entry);
    if (log.length > MAX_LOG) log.shift();
    if (echo)
        // eslint-disable-next-line
        console.info('[DragMonitor]', type, entry);
    persistLog();
    return entry;
}

/*
 * The stuck state observed in the field is PER-TAB and SURVIVES refresh (a
 * poisoned tab freezes on every drag until closed; a new tab is fine). That
 * lifetime matches Chrome's browser-side per-tab drag bookkeeping — and it
 * also matches sessionStorage exactly, so the log is mirrored there: after a
 * refresh the previous page-load's entries are restored, letting a dump show
 * the ORIGINAL poisoning drag, not just the post-refresh freezes.
 */
let persistTimer = null;
function persistLog() {
    if (persistTimer) return;
    persistTimer = setTimeout(() => {
        persistTimer = null;
        try { sessionStorage.setItem('__dragMonitorLog', JSON.stringify(log.slice(-MAX_LOG))); } catch (e) { /* full/blocked */ }
    }, 400);
}
function restoreLog() {
    try {
        const prev = JSON.parse(sessionStorage.getItem('__dragMonitorLog') || '[]');
        if (!prev.length) return;
        prev.forEach(e => { e.prevLoad = true; log.push(e); });
        seq = Math.max(seq, ...prev.map(e => e.n || 0));
        log.push({n: ++seq, t: new Date().toISOString(), type: 'log-restored-from-previous-page-load', entries: prev.length});
    } catch (e) { /* ignore */ }
}

function flushHeartbeat() {
    if (session && session.pendingMoves > 0) {
        push('heartbeat', {
            dragId: session.id,
            moves: session.pendingMoves,
            lastXY: session.lastXY,
            overTarget: session.lastOverTarget
        });
        session.pendingMoves = 0;
    }
}

function endSession(how, ev) {
    if (!session) {
        // dragend/drop with no open session is itself worth knowing about
        push(`${how}-without-start`, {target: describeEl(ev && ev.target)}, true);
        return;
    }
    flushHeartbeat();
    push(how, {
        dragId: session.id,
        durationMs: now() - session.startedAt,
        totalMoves: session.totalMoves,
        stalls: session.stalls,
        sourceConnected: session.sourceEl ? session.sourceEl.isConnected : null,
        target: describeEl(ev && ev.target),
        lastXY: session.lastXY
    }, true);
    session = null;
    if (watchdog) { clearInterval(watchdog); watchdog = null; }
    stopMutationWatch();
}

// --- DOM-mutation-during-drag watcher (see onDragStart) ---
let mutObserver = null;
function startMutationWatch(sourceEl, sess) {
    stopMutationWatch();
    if (typeof MutationObserver === 'undefined' || !sourceEl) return;
    // Chromium #168544 aborts a drag on ANY page DOM change during dragstart,
    // not only on the source. So watch the WHOLE document (subtree) during the
    // drag; `offsetMs` shows whether the mutation landed in the critical early
    // window, `isSource`/`isAncestor` mark whether it touched the drag source.
    const ancestors = new Set();
    for (let el = sourceEl; el && el !== document.documentElement; el = el.parentElement) ancestors.add(el);
    let logged = 0;
    mutObserver = new MutationObserver(muts => {
        if (!session || session.id !== sess.id || logged >= 12) return;
        for (const m of muts) {
            const t = m.target;
            const el = t.nodeType === 1 ? t : t.parentElement;
            logged++;
            push('DOM-mutation-during-drag', {
                dragId: sess.id,
                offsetMs: now() - sess.startedAt,
                kind: m.type,                       // 'attributes' | 'childList' | 'characterData'
                attr: m.attributeName || null,
                node: describeEl(el),
                isSource: t === sourceEl,
                isAncestor: ancestors.has(el),
                added: m.addedNodes ? m.addedNodes.length : 0,
                removed: m.removedNodes ? m.removedNodes.length : 0
            }, true);
            if (logged >= 12) break;
        }
    });
    try {
        mutObserver.observe(document.documentElement, {attributes: true, childList: true, subtree: true, characterData: true});
    } catch (e) { /* ignore */ }
}
function stopMutationWatch() {
    if (mutObserver) { try { mutObserver.disconnect(); } catch (e) { /* ignore */ } mutObserver = null; }
}

function onDragStart(ev) {
    // A second dragstart while a session is open means the previous drag
    // never delivered its dragend — exactly the stuck case. Log loudly.
    if (session)
        push('start-while-open', {previousDragId: session.id, openForMs: now() - session.startedAt}, true);

    session = {
        id: ++seq,
        startedAt: now(),
        lastHeartbeat: now(),
        totalMoves: 0,
        pendingMoves: 0,
        stalls: 0,
        stallNotified: false,
        sourceEl: ev.target,
        lastXY: {x: ev.clientX, y: ev.clientY},
        lastOverTarget: null
    };
    push('dragstart', {
        dragId: session.id,
        source: describeEl(ev.target),
        xy: session.lastXY,
        types: ev.dataTransfer ? [...(ev.dataTransfer.types || [])] : null
    }, true);

    // Chrome fires dragend immediately if the DOM/CSS is mutated while a drag
    // is being established (chromium #168544 / react-dnd #1085). Our logs show
    // drags self-aborting ~100ms after start, so SOMETHING is mutating during
    // the drag. Watch the source + its ancestor chain during the drag and log
    // the exact mutation with its ms-offset — that names the culprit so we can
    // defer it. Observer is torn down in endSession.
    startMutationWatch(ev.target, session);

    // Smoking-gun probe: Chrome silently kills a drag (no dragover, no
    // dragend, input state can wedge) when the source node is detached while
    // the drag is being established. A Vue re-render queued by a dragstart
    // handler flushes on the MICROTASK queue — before the browser continues —
    // so check connectivity right then, and again shortly after.
    const src = ev.target, id = session.id;
    Promise.resolve().then(() => {
        if (!src.isConnected) push('source-detached-at-microtask', {dragId: id}, true);
    });
    setTimeout(() => {
        if (session && session.id === id && !src.isConnected)
            push('source-detached-at-50ms', {dragId: id}, true);
    }, 50);

    if (watchdog) clearInterval(watchdog);
    watchdog = setInterval(() => {
        if (!session) { clearInterval(watchdog); watchdog = null; return; }
        flushHeartbeat();
        const silentFor = now() - session.lastHeartbeat;
        if (silentFor > STALL_AFTER_MS) {
            session.stalls++;
            // keep logging input evidence for a while after this drag ends
            postMortemUntil = now() + 120000;
            push('STALL', {
                dragId: session.id,
                silentForMs: silentFor,
                openForMs: now() - session.startedAt,
                sourceConnected: session.sourceEl ? session.sourceEl.isConnected : null,
                lastXY: session.lastXY
            }, true);
            // One friendly hint per drag; a silent period is also normal when
            // the cursor left the browser window, so phrase it as a maybe.
            if (!session.stallNotified) {
                session.stallNotified = true;
                snack.info('If clicks stop responding after dragging: press Esc, then click once. Over remote desktop, also try Alt+Tab away and back');
            }
            if (silentFor > HARD_STUCK_AFTER_MS) {
                push('STUCK-HARD', {dragId: session.id, silentForMs: silentFor}, true);
                clearInterval(watchdog);
                watchdog = null;
            }
        }
    }, HEARTBEAT_CHECK_MS);
}

function onMove(ev) {
    if (!session) return;
    session.lastHeartbeat = now();
    session.totalMoves++;
    session.pendingMoves++;
    session.lastXY = {x: ev.clientX, y: ev.clientY};
    if (ev.type === 'dragover') {
        const d = describeEl(ev.target);
        session.lastOverTarget = d ? `${d.tag}.${d.cls.split(' ')[0] || ''}` : null;
    }
}

function onKeyDown(ev) {
    if (ev.key === 'Escape' && (session || inPostMortem()))
        push('escape-pressed', {dragId: session ? session.id : null, duringSession: !!session}, true);
}

/*
 * Input-path evidence (added after the 2026-09-14 AnyDesk incident where the
 * app stayed click-dead and even Esc did not recover it). The open question
 * there: do pointer events still REACH the page (an in-page click-eater) or
 * does input stop arriving entirely (remote-desktop mouse-button desync at OS
 * level, where the host believes the button is still held)? Logging pointer
 * events while a drag session is open — and for a post-mortem window after a
 * stall — answers that:
 *  - pointer events logged with buttons=1 while nothing is pressed remotely
 *    => host thinks the button is stuck down (AnyDesk lost the mouse-up)
 *  - clicks logged but the UI ignores them  => in-page problem, investigate app
 *  - nothing logged at all                  => input never reaches the page
 */
let postMortemUntil = 0;
function inPostMortem() { return now() < postMortemUntil; }
let lastMoveSample = 0;

function onPointerEvidence(ev) {
    const relevant = session || inPostMortem();
    if (!relevant) return;
    if (ev.type === 'mousemove' || ev.type === 'pointermove') {
        // sampled: one entry per second is plenty to show buttons state
        if (now() - lastMoveSample < 1000) return;
        lastMoveSample = now();
    }
    push(`input-${ev.type}`, {
        dragId: session ? session.id : null,
        xy: {x: ev.clientX, y: ev.clientY},
        buttons: ev.buttons,
        target: describeEl(ev.target)
    }, true);
    // any pointer press/release while a drag session is open means the input
    // path is alive even though the drag went silent — log the contrast loudly
    if (session && (ev.type === 'mousedown' || ev.type === 'mouseup'))
        push('input-alive-during-stuck-drag', {dragId: session.id, openForMs: now() - session.startedAt}, true);
}

/*
 * Stuck-Sortable detection + auto-heal (added after the freeze reproduced in
 * the workflow editor's rules screen, 2026-09-15 — cursor stuck as a pointer,
 * clicks dead everywhere, scroll alive). vuedraggable/Sortable 1.10 drags can
 * wedge when the list re-renders mid-drag: cleanup is skipped, the ghost/clone
 * stays glued under the cursor swallowing every click, and Sortable's internal
 * "active" state persists. Unlike a native-drag wedge this IS recoverable from
 * JS: force Sortable's drop handling and remove the leftovers.
 */
let stuckSweeps = 0;
let lastButtons = 0;  // last known mouse-button bitmask (updated on any mousemove)

function sortableArtifacts() {
    const leftovers = document.querySelectorAll('.sortable-ghost, .sortable-fallback, .sortable-drag');
    return {
        active: Sortable ? Sortable.active || null : null,
        ghost: Sortable ? Sortable.ghost || null : null,
        leftovers,
        any: !!((Sortable && (Sortable.active || Sortable.ghost)) || leftovers.length)
    };
}

function sweepStuckSortable() {
    const art = sortableArtifacts();
    if (!art.any) { stuckSweeps = 0; return; }

    // A healthy native-drag session heartbeats continuously — don't touch it.
    if (session && now() - session.lastHeartbeat < 2500) { stuckSweeps = 0; return; }
    // A held mouse button means a legitimate mouse-based drag is in progress.
    if (lastButtons !== 0) { stuckSweeps = 0; return; }

    stuckSweeps++;
    if (stuckSweeps === 1)
        push('sortable-artifact-detected', {
            activeList: art.active ? describeEl(art.active.el) : null,
            ghost: art.ghost ? describeEl(art.ghost) : null,
            leftoverCount: art.leftovers.length,
            sessionOpen: !!session
        }, true);
    if (stuckSweeps < SWEEPS_BEFORE_HEAL) return;

    // ---- auto-heal ----
    const healed = {droppedActive: false, removedNodes: 0, strippedClasses: 0};
    try {
        if (art.active && typeof art.active._onDrop === 'function') {
            art.active._onDrop(null);   // runs Sortable's full cleanup path
            healed.droppedActive = true;
        }
    } catch (e) { push('sortable-heal-onDrop-error', {msg: String(e).slice(0, 120)}, true); }
    // remove any clone/ghost nodes cleanup left behind
    document.querySelectorAll('.sortable-fallback, .sortable-ghost').forEach(el => {
        try { el.parentNode && el.parentNode.removeChild(el); healed.removedNodes++; } catch (e) { /* ignore */ }
    });
    document.querySelectorAll('.sortable-chosen, .sortable-drag').forEach(el => {
        el.classList.remove('sortable-chosen', 'sortable-drag', 'sortable-ghost');
        healed.strippedClasses++;
    });
    // restore body-level styles Sortable toggles during drags
    try {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
    } catch (e) { /* ignore */ }

    stuckSweeps = 0;
    postMortemUntil = now() + 120000;
    push('sortable-stuck-HEALED', healed, true);
    snack.info('Recovered a stuck drag — you can carry on working');
}

function installDragMonitor() {
    if (window.__dragMonitorInstalled) return;
    window.__dragMonitorInstalled = true;

    document.addEventListener('dragstart', onDragStart, true);
    document.addEventListener('drag', onMove, true);
    document.addEventListener('dragover', onMove, true);
    document.addEventListener('dragenter', ev => {
        if (session) session.lastHeartbeat = now();
        // dragenter with files and no session = an OS file drag entering the window
        else if (ev.dataTransfer && [...(ev.dataTransfer.types || [])].includes('Files'))
            push('os-file-drag-entered', {target: describeEl(ev.target)}, true);
    }, true);
    document.addEventListener('dragend', ev => endSession('dragend', ev), true);
    document.addEventListener('drop', ev => endSession('drop', ev), true);
    document.addEventListener('keydown', onKeyDown, true);
    ['mousedown', 'mouseup', 'click', 'mousemove'].forEach(t =>
        document.addEventListener(t, onPointerEvidence, true));
    // always-on, logging-free: track button state for the stuck-Sortable sweep
    document.addEventListener('mousemove', ev => { lastButtons = ev.buttons; }, {capture: true, passive: true});
    document.addEventListener('mouseup', () => { lastButtons = 0; }, {capture: true, passive: true});
    setInterval(sweepStuckSortable, SWEEP_MS);

    restoreLog();

    window.__dragLog = log;
    window.__dragLogDump = () => JSON.stringify(log, null, 1);
    window.__dragStatus = () => ({
        sessionOpen: !!session,
        dragId: session ? session.id : null,
        openForMs: session ? now() - session.startedAt : null,
        stalls: session ? session.stalls : null,
        postMortem: inPostMortem(),
        entries: log.length
    });

    let navType = null;
    try { navType = performance.getEntriesByType('navigation')[0].type; } catch (e) { /* n/a */ }
    push('monitor-installed', {ua: navigator.userAgent.slice(0, 120), navType}, true);
}

module.exports = {installDragMonitor};
